// Party toast overlay — extracted from party-ui.ts (Phase 7 Block F §10 400-line cap).
// ToastOverlay renders stacked party-invite toasts in the top-center of the screen.

import { Application, Container, Graphics, Text } from "pixi.js";
import { TOAST_LIFETIME_MS } from "./party-state.ts";

const TOAST_W = 320; // contracts:allow-literal — toast width
const TOAST_H = 40; // contracts:allow-literal — toast height
const TOAST_GAP = 8; // contracts:allow-literal — stacking gap
const TOAST_BG = 0x1a1a3a;
const TOAST_BORDER = 0x4455cc;
const TOAST_TEXT = 0xffffaa;

export type ToastOverlayDeps = {
  app: Application;
  onToastClick: (contextId: string | undefined) => void;
};

export class ToastOverlay {
  private readonly container = new Container();
  private readonly active = new Map<string, { root: Container; spawnedAt: number }>();

  constructor(private readonly deps: ToastOverlayDeps) {
    this.container.zIndex = 110; // contracts:allow-literal — above HUD, below panel
    deps.app.stage.addChild(this.container);
    this.layout();
    deps.app.renderer.on("resize", () => this.layout());
  }

  // Called from main.ts ticker each frame. Spawns nodes for new toasts,
  // GCs nodes whose entries have aged out of state, and fades in-flight.
  tick(toasts: ReadonlyArray<{ id: string; text: string; contextId?: string; spawnedAt: number }>, now: number): void {
    const live = new Set<string>();
    let stackIdx = 0;
    for (const entry of toasts) {
      live.add(entry.id);
      let rec = this.active.get(entry.id);
      if (rec === undefined) {
        rec = { root: this.buildToast(entry.text, entry.contextId), spawnedAt: entry.spawnedAt };
        this.container.addChild(rec.root);
        this.active.set(entry.id, rec);
      }
      const age = now - rec.spawnedAt;
      rec.root.alpha = Math.max(0, 1 - age / TOAST_LIFETIME_MS);
      rec.root.x = 0;
      rec.root.y = stackIdx * (TOAST_H + TOAST_GAP);
      stackIdx += 1;
    }
    for (const [id, rec] of this.active) {
      if (!live.has(id)) {
        rec.root.destroy();
        this.active.delete(id);
      }
    }
  }

  private buildToast(text: string, contextId: string | undefined): Container {
    const root = new Container();
    root.eventMode = "static";
    root.cursor = "pointer";
    const bg = new Graphics()
      .rect(0, 0, TOAST_W, TOAST_H)
      .fill({ color: TOAST_BG })
      .stroke({ color: TOAST_BORDER, width: 1 }); // contracts:allow-literal — toast border
    root.addChild(bg);
    const t = new Text({
      text,
      style: { fontSize: 13, fill: TOAST_TEXT, fontFamily: "monospace" }, // contracts:allow-literal — toast font
    });
    t.x = 10; // contracts:allow-literal — left padding
    t.y = (TOAST_H - t.height) / 2; // contracts:allow-literal — centering idiom
    root.addChild(t);
    root.on("pointerdown", () => this.deps.onToastClick(contextId));
    return root;
  }

  private layout(): void {
    const screenW = this.deps.app.renderer.width;
    this.container.x = (screenW - TOAST_W) / 2; // contracts:allow-literal — centering idiom
    this.container.y = 16; // contracts:allow-literal — top margin
  }
}
